import { Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import Swal from "sweetalert2";

import { ApiErrorService } from "src/app/core/services/api-error.service";
import { ProposalsService } from "src/app/pages/proposals/services/proposals.service";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Enviar para o promotor" (propostas de frações, antes de aceitar).
 *
 * O servidor pré-preenche Para (proprietários da fração e do empreendimento),
 * Assunto e Corpo; o utilizador confirma ou corrige e envia. As respostas do
 * promotor vão para o utilizador (`replyTo`). Depois do envio a proposta
 * passa a "Em Análise": fecha com `{ history }`, a nova entrada do histórico
 * (`null` num reenvio, quando já estava em análise).
 *
 * Abre-se com `NgbModal.open(SendToPromoterModalComponent)` e
 * `componentInstance.proposalId`.
 */
@Component({
  selector: "app-send-to-promoter-modal",
  templateUrl: "./send-to-promoter-modal.component.html",
})
export class SendToPromoterModalComponent implements OnInit {
  @Input() proposalId!: number;

  form!: FormGroup;
  loading = true;
  loadError = false;
  sending = false;
  submitted = false;
  /** Proprietários com e-mail (opções do ng-select). */
  ownerEmails: string[] = [];
  replyTo = "";
  /** O imóvel ainda pode passar a Reservado (não está Reservado/Vendido/Arrendado). */
  canReserve = false;

  constructor(
    public readonly activeModal: NgbActiveModal,
    private readonly formBuilder: FormBuilder,
    private readonly proposalsService: ProposalsService,
    private readonly translate: TranslateService,
    private readonly apiError: ApiErrorService,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      to: [[], [Validators.required]],
      subject: ["", [Validators.required, Validators.maxLength(255)]],
      body: ["", [Validators.required, Validators.maxLength(10000)]],
      // Pré-marcado: ao enviar ao promotor o imóvel passa a Reservado.
      reserveProperty: [true],
    });
    this.proposalsService.getPromoterEmailPreview(this.proposalId).subscribe({
      next: (preview) => {
        this.ownerEmails = preview?.to ?? [];
        this.replyTo = preview?.replyTo ?? "";
        this.canReserve = preview?.canReserve === true;
        this.form.patchValue({
          to: [...this.ownerEmails],
          subject: preview?.subject ?? "",
          body: preview?.body ?? "",
        });
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.loadError = true;
        this.apiError.show(error, {
          fallback: this.translate.instant("t-proposals.t-promoter-email-error"),
        });
      },
    });
  }

  /** `addTag` do ng-select: só e-mails válidos entram na lista. */
  addEmail = (term: string): string | null => {
    const email = `${term ?? ""}`.trim().toLowerCase();
    return EMAIL_PATTERN.test(email) ? email : null;
  };

  /** O que está escrito no campo Para e ainda não foi confirmado com Enter. */
  private pendingEmail = "";

  onEmailSearch(event: { term?: string } | null) {
    this.pendingEmail = `${event?.term ?? ""}`;
  }

  /**
   * Ao sair do campo, o ng-select descarta o texto escrito: um e-mail
   * válido ainda por confirmar entra na lista em vez de se perder.
   */
  commitPendingEmail() {
    const email = this.addEmail(this.pendingEmail);
    this.pendingEmail = "";
    if (!email) return;
    const current: string[] = this.form.value.to ?? [];
    if (current.includes(email)) return;
    this.form.patchValue({ to: [...current, email] });
  }

  get recipients(): string[] {
    return (this.form?.value?.to ?? []).filter((email: string) =>
      EMAIL_PATTERN.test(`${email ?? ""}`.trim()),
    );
  }

  get isValid(): boolean {
    return (
      this.recipients.length > 0 &&
      !!`${this.form?.value?.subject ?? ""}`.trim() &&
      !!`${this.form?.value?.body ?? ""}`.trim() &&
      this.form.valid
    );
  }

  send() {
    this.submitted = true;
    if (!this.isValid || this.sending) return;
    this.sending = true;
    this.proposalsService
      .sendPromoterEmail(this.proposalId, {
        to: this.recipients,
        subject: `${this.form.value.subject}`.trim(),
        body: this.form.value.body,
        reserveProperty: this.canReserve && this.form.value.reserveProperty === true,
      })
      .subscribe({
        next: (response) => {
          this.sending = false;
          Swal.fire({
            title: this.translate.instant("t-proposals.t-promoter-email-sent"),
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          this.activeModal.close({
            history: response?.history ?? null,
            reservedId: response?.reservedId ?? null,
          });
        },
        error: (error) => {
          this.sending = false;
          this.apiError.show(error, {
            fallback: this.translate.instant("t-proposals.t-promoter-email-error"),
          });
        },
      });
  }

  close() {
    this.activeModal.dismiss();
  }
}
